import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs, doc, getDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { toast } from 'react-toastify';
import { FaPlus, FaTimes, FaFilter, FaUser, FaSpinner, FaExclamationCircle, FaCheckCircle, FaPause, FaTimesCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Project {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  status: 'active' | 'completed' | 'on-hold' | 'cancelled';
  totalAmount: number;
  progress: number;
  startDate: Date | null;
  endDate: Date | null;
  createdAt: Date;
  updatedAt: Date;
  staff: any[];
}

interface Client {
  id: string;
  name: string;
  email: string;
}

const Projects: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const pageSize = 8;
  const [clientOptions, setClientOptions] = useState<string[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientId: '',
    totalAmount: 0,
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    if (userData?.language && i18n.language !== userData.language) {
      i18n.changeLanguage(userData.language);
    }
  }, [userData?.language, i18n]);

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!userData?.businessId) return;

      try {
        const clientsRef = collection(db, `clients/${userData.businessId}/clients`);
        const querySnapshot = await getDocs(clientsRef);
        
        const clientsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Client[];

        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, [userData?.businessId]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!userData?.businessId) return;

      try {
        setLoading(true);
        const projectsRef = collection(db, `projects/${userData.businessId}/projects`);
        const querySnapshot = await getDocs(projectsRef);
        
        const projectsData = await Promise.all(
          querySnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            // Obtener información del cliente
            const clientRef = doc(db, `clients/${userData.businessId}/clients/${data.clientId}`);
            const clientDoc = await getDoc(clientRef);
            const clientData = clientDoc.data();

            return {
              id: docSnapshot.id,
              name: data.name,
              clientId: data.clientId,
              clientName: clientData?.name || t('projects.unknownClient'),
              status: data.status,
              totalAmount: data.totalAmount,
              progress: data.progress,
              startDate: data.startDate?.toDate() || null,
              endDate: data.endDate?.toDate() || null,
              createdAt: data.createdAt.toDate(),
              updatedAt: data.updatedAt.toDate(),
              staff: data.staff || []
            };
          })
        );

        // Si el usuario es employee, filtrar solo los proyectos donde está asignado
        let filteredProjects = projectsData;
        if (userData.role !== 'admin' && userData.role !== 'business') {
          filteredProjects = projectsData.filter(project => {
            return project.staff?.some((member: any) => member.id === (userData as any).uid);
          });
        }

        setProjects(filteredProjects);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [userData?.businessId, userData?.role, (userData as any)?.uid, t]);

  // Obtener lista de clientes únicos para el filtro
  useEffect(() => {
    const clients = Array.from(new Set(projects.map(p => p.clientName)));
    setClientOptions(clients);
  }, [projects]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userData?.businessId) {
      toast.error(t('common.error'));
      return;
    }

    if (!formData.name || !formData.clientId) {
      toast.error(t('projects.validation.required'));
      return;
    }

    try {
      setCreatingProject(true);
      
      // Crear objeto del creador para agregarlo al staff
      const creatorStaff: any = {
        id: (userData as any).uid,
        firstName: (userData as any).firstName || '',
        lastName: (userData as any).lastName || '',
        email: (userData as any).email || '',
        role: userData.role || 'business'
      };

      // Agregar campos opcionales si existen
      if ((userData as any).photoURL) {
        creatorStaff.photoURL = (userData as any).photoURL;
      }

      const projectData = {
        name: formData.name,
        description: formData.description,
        clientId: formData.clientId,
        totalAmount: Number(formData.totalAmount),
        progress: 0,
        status: 'active',
        startDate: formData.startDate ? Timestamp.fromDate(new Date(formData.startDate)) : null,
        endDate: formData.endDate ? Timestamp.fromDate(new Date(formData.endDate)) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: (userData as any).uid, // Guardar quién creó el proyecto
        // Campos adicionales según la estructura
        columns: [
          { id: 'new', name: 'Nuevo' },
          { id: 'in_progress', name: 'En curso' },
          { id: 'done', name: 'Completada' }
        ],
        materials: [],
        notes: [],
        services: [],
        staff: [creatorStaff], // Asignar automáticamente al creador
        skills: [],
        tasks: []
      };

      const projectsRef = collection(db, `projects/${userData.businessId}/projects`);
      await addDoc(projectsRef, projectData);
      
      toast.success(t('projects.created'));
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        clientId: '',
        totalAmount: 0,
        startDate: '',
        endDate: ''
      });
      
      // Recargar proyectos
      window.location.reload();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(t('common.error'));
    } finally {
      setCreatingProject(false);
    }
  };

  const filtered = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const statusOk = statusFilter === 'all' || project.status === statusFilter;
    const clientOk = clientFilter === 'all' || project.clientName === clientFilter;
    return matchesSearch && statusOk && clientOk;
  });

  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  const getStatusText = (status: Project['status']) => {
    switch (status) {
      case 'active': return t('projects.status.active');
      case 'completed': return t('projects.status.completed');
      case 'on-hold': return t('projects.status.onHold');
      case 'cancelled': return t('projects.status.cancelled');
      default: return status;
    }
  };

  const getStatusBadgeClasses = (status: Project['status']) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: Project['status']) => {
    switch (status) {
      case 'active':
        return <FaCheckCircle className="text-blue-600" />;
      case 'completed':
        return <FaCheckCircle className="text-green-600" />;
      case 'on-hold':
        return <FaPause className="text-yellow-600" />;
      case 'cancelled':
        return <FaTimesCircle className="text-red-600" />;
      default:
        return <FaExclamationCircle className="text-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header mejorado */}
        <div className="bg-white rounded-3xl shadow-xl border border-white/40 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent mb-2">
                {t('projects.title')}
              </h1>
              <p className="text-gray-600">
                {projects.length} {projects.length === 1 ? t('projects.projectRegistered', 'proyecto registrado') : t('projects.projectsRegistered', 'proyectos registrados')}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <FaPlus className="text-lg" />
              {t('projects.create')}
            </button>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center shadow-xl border border-white/40">
            <div className="max-w-md mx-auto">
              <div className="w-32 h-32 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <FaPlus className="text-6xl text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {searchTerm || statusFilter !== 'all' || clientFilter !== 'all' 
                  ? t('projects.noResults') 
                  : t('projects.empty')}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || statusFilter !== 'all' || clientFilter !== 'all'
                  ? t('projects.adjustFilters')
                  : t('projects.startCreatingMessage', 'Comienza creando tu primer proyecto para gestionar tareas y recursos')}
              </p>
              {!searchTerm && statusFilter === 'all' && clientFilter === 'all' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  <FaPlus />
                  {t('projects.createFirst', 'Crear primer proyecto')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Filtros */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    <FaFilter className="inline mr-1" />
                    {t('projects.filter')}
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                  >
                    <option value="all">{t('projects.allStatuses')}</option>
                    <option value="active">{t('projects.status.active')}</option>
                    <option value="completed">{t('projects.status.completed')}</option>
                    <option value="on-hold">{t('projects.status.onHold')}</option>
                    <option value="cancelled">{t('projects.status.cancelled')}</option>
                  </select>
                </div>
                
                <div className="flex-1">
                  <label className="block text-sm font-medium text-red-600 mb-2">
                    <FaUser className="inline mr-1" />
                    {t('projects.client')}
                  </label>
                  <select
                    value={clientFilter}
                    onChange={(e) => { setClientFilter(e.target.value); setCurrentPage(1); }}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                  >
                    <option value="all">{t('projects.allClients')}</option>
                    {clientOptions.map(client => (
                      <option key={client} value={client}>{client}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Grid de Proyectos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((project) => (
                <div
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl border border-gray-100 hover:border-red-200 transition-all cursor-pointer transform hover:scale-105 relative overflow-hidden"
                >
                  {/* Decoración de fondo */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-50 to-pink-50 rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                  
                  <div className="relative">
                    {/* Header con nombre y estado */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg group-hover:text-red-600 transition-colors line-clamp-2 mb-2">
                          {project.name}
                        </h3>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClasses(project.status)}`}>
                          {getStatusIcon(project.status)}
                          <span className="ml-1">{getStatusText(project.status)}</span>
                        </span>
                      </div>
                    </div>

                    {/* Información del proyecto */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-lg">👤</span>
                        <span className="truncate font-medium">{project.clientName}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-lg">💰</span>
                        <span className="font-medium">${project.totalAmount.toLocaleString()}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-lg">📅</span>
                        <span className="font-medium">
                          {project.startDate 
                            ? format(project.startDate, 'dd/MM/yyyy', { locale: es })
                            : t('projects.notStarted')
                          }
                        </span>
                      </div>

                      {/* Barra de progreso */}
                      <div className="pt-2">
                        <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                          <span>{t('projects.progress', 'Progreso')}</span>
                          <span className="font-semibold">{project.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-red-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Footer con fecha de creación */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>
                          {t('projects.createdOn', 'Creado')} {format(project.createdAt, 'dd/MM/yyyy', { locale: es })}
                        </span>
                        <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors">
                          <span className="text-red-600 text-xs">→</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-8">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-red-600 text-red-600 rounded-lg font-medium transition-colors duration-200 hover:bg-red-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('projects.pagination.prev')}
                </button>
                
                <span className="text-gray-700 font-medium">
                  {currentPage} {t('projects.pagination.of')} {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-red-600 text-red-600 rounded-lg font-medium transition-colors duration-200 hover:bg-red-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('projects.pagination.next')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Crear Proyecto */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{t('projects.create')}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreateProject} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('projects.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('projects.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={t('projects.descriptionPlaceholder')}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 resize-vertical"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('projects.client')} *
                </label>
                <select
                  value={formData.clientId}
                  onChange={(e) => setFormData({...formData, clientId: e.target.value})}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                >
                  <option value="">{t('projects.selectClient')}</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('projects.totalAmount')}
                </label>
                <input
                  type="number"
                  value={formData.totalAmount}
                  onChange={(e) => setFormData({...formData, totalAmount: Number(e.target.value)})}
                  min="0"
                  step="0.01"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projects.startDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projects.endDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creatingProject}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors duration-200 flex items-center"
                >
                  {creatingProject ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : (
                    <FaPlus className="mr-2" />
                  )}
                  {creatingProject ? t('common.creating') : t('projects.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects; 